<template>
  <div>
    <v-layout wrap>
      <v-flex xs12 sm8 md9>
        <v-text-field 
          v-model="searchQuery" 
          :label="searchPlaceholder ? `${$t('timeline.search_location')} (${searchPlaceholder})` : $t('timeline.search_location')" 
          @keyup.enter="search"
          prepend-inner-icon="fas fa-search"
          solo
          flat
          rounded
          hide-details
          :loading="loading"
          class="mb-3"
        ></v-text-field>
      </v-flex>
      <v-flex xs12 sm4 md3 class="pl-sm-3">
        <v-select
          v-model="searchField"
          :items="searchFields"
          solo
          flat
          rounded
          hide-details
          class="mb-3"
        ></v-select>
      </v-flex>
    </v-layout>

    <v-layout wrap style="margin-top: -15px; margin-bottom: 15px;">
      <v-flex xs12>
        <v-menu
          v-model="menuStart"
          :close-on-content-click="false"
          offset-y
        >
          <template v-slot:activator="{ on, attrs }">
            <v-chip
              class="ma-2"
              color="primary"
              outlined
              v-bind="attrs"
              v-on="on"
            >
              <i class="fad fa-calendar" style="margin-right: 5px;"></i>
              {{ $t('timeline.from') }}: {{ startDate || $t('timeline.any') }}
            </v-chip>
          </template>
          <v-date-picker
            v-model="startDate"
            @input="menuStart = false"
            :first-day-of-week="1"
          ></v-date-picker>
        </v-menu>

        <v-menu
          v-model="menuEnd"
          :close-on-content-click="false"
          offset-y
        >
          <template v-slot:activator="{ on, attrs }">
            <v-chip
              class="ma-2"
              color="primary"
              outlined
              v-bind="attrs"
              v-on="on"
            >
              <i class="fad fa-calendar" style="margin-right: 5px;"></i>
              {{ $t('timeline.to') }}: {{ endDate || $t('timeline.any') }}
            </v-chip>
          </template>
          <v-date-picker
            v-model="endDate"
            @input="menuEnd = false"
            :first-day-of-week="1"
          ></v-date-picker>
        </v-menu>

        <v-chip
          v-if="startDate || endDate"
          class="ma-2"
          color="error"
          outlined
          @click="startDate = ''; endDate = '';"
        >
          <i class="fad fa-times" style="margin-right: 5px;"></i>
          {{ $t('timeline.clear_dates') }}
        </v-chip>

        <v-btn color="primary" @click="search" :loading="loading" class="ma-2" rounded>
          {{ $t('timeline.apply_filters') }}
        </v-btn>
      </v-flex>
    </v-layout>

    <div v-if="loading" class="empty_map d-flex flex-column align-center justify-center">
      <div class="radar-spinner">
        <div class="radar-dot">
          <i class="fas fa-map-marker-alt"></i>
        </div>
        <div class="radar-ring radar-ring-1"></div>
        <div class="radar-ring radar-ring-2"></div>
      </div>
      <div class="mt-8 text-h6" style="font-weight: 600; opacity: 0.7; letter-spacing: 0.5px;">
        {{ $t('general.search') }}...
      </div>
    </div>
    <div v-else>
      <GmapMap
        v-if="locations.length !== 0"
        :center="map_center"
        :zoom="map_zoom"
        map-type-id="roadmap"
        :options="mapOptions"
        style="width: calc(100% + 2 * 12px); height: 400px; margin: -12px"
      >
        <gmap-cluster>
          <GmapMarker
            v-for="location in pointLocations"
            :position="google && new google.maps.LatLng(location.location)"
            :key="location.id"
          ></GmapMarker>
        </gmap-cluster>
      </GmapMap>
      <div class="empty_map" v-else></div>
    </div>

    <div v-if="!loading">
      <div v-if="searched && locations.length === 0" class="d-flex flex-column align-center justify-center py-12 px-4 text-center mt-4 mb-4">
        <v-avatar color="grey lighten-4" size="100" class="mb-6">
          <i class="fad fa-map-marked-alt fa-3x grey--text text--darken-1"></i>
        </v-avatar>
        <h3 class="text-h5 font-weight-bold grey--text text--darken-3 mb-3">
          {{ $t('timeline.no_locations_found') }}
        </h3>
        <p class="text-body-1 grey--text text--darken-1 mb-8" style="max-width: 400px; margin: 0 auto;">
          {{ $t('timeline.no_locations_hint') }}
        </p>
        <v-btn
          color="primary"
          outlined
          rounded
          class="font-weight-bold px-8"
          @click="resetSearch"
        >
          <i class="fad fa-undo-alt mr-2"></i> {{ $t('timeline.reset_search') }}
        </v-btn>
      </div>
      <div v-else-if="locations.length > 0" style="margin-top: 30px;">
        <!-- Memory Insight Panel -->
        <v-card v-if="isMemory" class="mb-6 elevation-2" style="border-left: 4px solid #9C27B0;">
          <v-card-text>
            <v-layout row wrap align-center>
              <v-flex xs12 sm2 class="text-center d-none d-sm-block">
                <i class="fad fa-calendar-star fa-3x" style="color: #9C27B0;"></i>
              </v-flex>
              <v-flex xs12 sm10>
                <div class="text-h6 mb-1">
                  {{ memoryYearsAgo === 1 ? $t('timeline.memory_year_ago') : memoryYearsAgo ? $t('timeline.memory_years_ago', { years: memoryYearsAgo }) : $t('timeline.random_memory') }}
                </div>
                <div class="subtitle-1">
                  <span @click="openMemoryDay" style="cursor: pointer;" class="insight-period-item">
                    {{ $t('timeline.you_were_here_on') }} <strong>{{ formatDate(memoryDate) }}</strong>.
                  </span>
                  <ul class="mt-2 mb-2">
                    <li v-for="(loc, idx) in uniqueMemoryAddresses" :key="idx">
                      {{ loc }}
                    </li>
                  </ul>
                  <span class="text-caption grey--text">{{ $t('timeline.total') }}: {{ locations.length }} {{ $t('timeline.recorded_points') }}.</span>
                </div>
              </v-flex>
            </v-layout>
          </v-card-text>
        </v-card>

        <v-card v-if="!isMemory && searchInsights" class="mb-6 elevation-2" style="border-left: 4px solid #FFB024;">
          <v-card-text>
            <v-layout row wrap align-center>
              <v-flex xs12 sm2 class="text-center d-none d-sm-block">
                <i class="fad fa-lightbulb fa-3x" style="color: #FFB024;"></i>
              </v-flex>
              <v-flex xs12 sm10>
                <div class="text-h6 mb-1">{{ $t('timeline.insights_for') }} "{{ lastSearchQuery }}"</div>
                <div class="subtitle-1">
                  <div v-if="searchInsights.periods.length === 1">
                    <span v-if="searchInsights.periods[0].firstDate === searchInsights.periods[0].lastDate" @click="openDailyView(searchInsights.periods[0])" style="cursor: pointer;" class="insight-period-item">
                      {{ $t('timeline.you_were_here_on') }} <strong>{{ searchInsights.periods[0].firstDate }}</strong>.
                    </span>
                    <span v-else @click="openDailyView(searchInsights.periods[0])" style="cursor: pointer;" class="insight-period-item">
                      {{ $t('timeline.you_were_here_from') }} <strong>{{ searchInsights.periods[0].firstDate }}</strong> {{ $t('timeline.to').toLowerCase() }} <strong>{{ searchInsights.periods[0].lastDate }}</strong>.
                    </span>
                  </div>
                  <div v-else>
                    {{ $t('timeline.you_visited_here_across') }} <strong>{{ searchInsights.periods.length }}</strong> {{ $t('timeline.different_periods') }}
                    <ul class="mt-2 mb-2">
                      <li v-for="(period, index) in searchInsights.periods" :key="index" @click="openDailyView(period)" style="cursor: pointer;" class="insight-period-item">
                        <span v-if="period.firstDate === period.lastDate">
                          <strong>{{ period.firstDate }}</strong>
                        </span>
                        <span v-else>
                          <strong>{{ period.firstDate }}</strong> {{ $t('timeline.to').toLowerCase() }} <strong>{{ period.lastDate }}</strong>
                        </span>
                        <span class="grey--text text-caption ml-2">({{ period.count }} {{ $t('timeline.points') }})</span>
                      </li>
                    </ul>
                  </div>
                  <span class="text-caption grey--text">{{ $t('timeline.total') }}: {{ searchInsights.totalPoints }} {{ $t('timeline.recorded_points') }}.</span>
                </div>
              </v-flex>
            </v-layout>
          </v-card-text>
        </v-card>

        <v-layout row justify-space-between class="mb-4 mx-0">
          <h3 style="padding-left: 12px">{{ $t('timeline.results') }} ({{ locations.length }} {{ $t('timeline.points') }})</h3>
          <v-btn text color="primary" @click="switchView" style="margin-right: 12px">
            <i class="fad" :class="currentView === 'aggregated' ? 'fa-list' : 'fa-layer-group'" style="margin-right: 5px;"></i>
            {{ $t(currentView === 'aggregated' ? 'timeline.switch_to_list_view' : 'timeline.switch_to_aggregated_view') }}
          </v-btn>
        </v-layout>

        <v-timeline dense align-top v-if="currentView === 'individual'">
          <PointSingle
            v-for="(location, index) in locations"
            :key="location.id"
            :location="location"
            :index="index"
            @delete="deletePoint"
          ></PointSingle>
        </v-timeline>

        <v-timeline dense align-top v-if="currentView === 'aggregated'">
          <v-timeline-item
            v-for="(group, index) in aggregatedLocations"
            :key="index"
            class="timeline-item-animated"
            :style="{ 'animation-delay': (index * 0.05) + 's' }"
            color="primary"
            icon="fas fa-calendar-day"
            fill-dot
            small
          >
            <v-card class="elevation-2 timeline-card">
              <v-card-title class="text-subtitle-1 font-weight-bold pb-1 pt-3">
                <span class="primary--text">{{ formatDate(group.date) }}</span>
              </v-card-title>
              <v-card-text class="pb-3">
                <div class="text-body-1 font-weight-medium grey--text text--darken-3">{{ group.count }} {{ $t('timeline.recorded_points') }}</div>
                <div class="grey--text mt-2 text-caption">{{ group.firstPoint.data.address }}</div>
              </v-card-text>
            </v-card>
          </v-timeline-item>
        </v-timeline>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import { storage } from "../schmucklicloud";
import Location from "../logic/Location";

import PointSingle from "../components/timeline/PointSingle";
import { Notifications } from "../notification";

import { gmapApi } from "vue2-google-maps";
import GmapCluster from "vue2-google-maps/dist/components/cluster";
import { getMapOptions } from "../mapStyles";
Vue.component("GmapCluster", GmapCluster);

export default {
  data() {
    return {
      searchQuery: "",
      searchPlaceholder: "",
      availableCities: [],
      availableCountries: [],
      searchField: "city",
      lastSearchQuery: "",
      startDate: "",
      endDate: "",
      
      menuStart: false,
      menuEnd: false,

      loading: false,
      searched: false,

      isMemory: false,
      memoryDate: null,
      memoryYearsAgo: null,
      uniqueMemoryAddresses: [],

      currentView: "aggregated",

      locations: [],
      aggregatedLocations: [],
      path: [],
    };
  },
  async mounted() {
    if (this.$route.query.field) {
      this.searchField = this.$route.query.field;
    }
    if (this.$route.query.start) {
      this.startDate = this.$route.query.start;
    }
    if (this.$route.query.end) {
      this.endDate = this.$route.query.end;
    }
    if (this.$route.query.q) {
      this.searchQuery = this.$route.query.q;
      await this.search();
    } else {
      await this.loadInitialMemory();
    }
    this.loadPlaceholderOptions();
  },
  watch: {
    searchField() {
      this.updatePlaceholder();
    }
  },
  computed: {
    searchFields() {
      return [
        { text: this.$t("timeline.search_fields.city"), value: 'city' },
        { text: this.$t("timeline.search_fields.address"), value: 'address' },
        { text: this.$t("timeline.search_fields.country"), value: 'country' }
      ];
    },
    google: gmapApi,
    mapOptions() {
      return getMapOptions(this.$vuetify.theme.dark);
    },
    map_zoom() {
      if (this.path.length === 0) return 2;
      return this.getZoomLevel(this.path);
    },
    map_center() {
      if (this.path.length === 0) return { lat: 0, lng: 0 };
      return this.getCenterFromCoordinates(this.path);
    },
    pointLocations() {
      var locations = [];
      for (let location of this.locations) {
        locations.push({
          id: location.id,
          location: {
            lat: parseFloat(location.data.lat),
            lng: parseFloat(location.data.lon),
          },
          weight: 100,
        });
      }
      return locations;
    },
    searchInsights() {
      if (!this.locations || this.locations.length === 0) return null;
      
      let periods = [];
      let currentPeriod = null;
      
      this.locations.forEach(loc => {
        let locDate = new Date(loc.data.date);
        
        if (!currentPeriod) {
          currentPeriod = {
            firstDate: loc.data.date,
            lastDate: loc.data.date,
            firstDateObj: locDate,
            lastDateObj: locDate,
            count: 1
          };
        } else {
          let diffTime = Math.abs(locDate - currentPeriod.lastDateObj);
          let diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          
          if (diffDays > 30) {
            periods.push(currentPeriod);
            currentPeriod = {
              firstDate: loc.data.date,
              lastDate: loc.data.date,
              firstDateObj: locDate,
              lastDateObj: locDate,
              count: 1
            };
          } else {
            currentPeriod.lastDate = loc.data.date;
            currentPeriod.lastDateObj = locDate;
            currentPeriod.count++;
          }
        }
      });
      
      if (currentPeriod) {
        periods.push(currentPeriod);
      }
      
      periods = periods.map(p => ({
        firstDate: this.formatDate(p.firstDate),
        lastDate: this.formatDate(p.lastDate),
        rawFirstDate: p.firstDate,
        rawLastDate: p.lastDate,
        count: p.count
      }));
      
      return {
        periods: periods,
        totalPoints: this.locations.length,
      };
    }
  },
  methods: {
    loadPlaceholderOptions() {
      try {
        let cities = new Set();
        let countries = new Set();

        if (this.locations && this.locations.length > 0) {
          for (let loc of this.locations) {
            if (loc && loc.data && loc.data.address) {
              let parts = loc.data.address.split(',');
              if (parts.length > 0) {
                let cityPart = parts.length > 1 ? parts[parts.length - 2] : parts[0];
                cityPart = cityPart.trim().replace(/[0-9]/g, '').trim();
                if (cityPart) cities.add(cityPart);

                let countryPart = parts[parts.length - 1];
                countryPart = countryPart.trim().replace(/[0-9]/g, '').trim();
                if (countryPart) countries.add(countryPart);
              }
            }
          }
        }

        if (cities.size === 0) {
          cities.add("Zürich");
          cities.add("Bern");
          cities.add("Basel");
        }
        if (countries.size === 0) {
          countries.add("Switzerland");
          countries.add("Germany");
          countries.add("Austria");
        }

        this.availableCities = Array.from(cities);
        this.availableCountries = Array.from(countries);
        this.updatePlaceholder();
      } catch (err) {
        console.error("Error loading placeholder options:", err);
      }
    },
    updatePlaceholder() {
      if (this.searchField === 'city' && this.availableCities.length > 0) {
        let randomCity = this.availableCities[Math.floor(Math.random() * this.availableCities.length)];
        this.searchPlaceholder = this.$t('timeline.e_g', { location: randomCity });
      } else if (this.searchField === 'country' && this.availableCountries.length > 0) {
        let randomCountry = this.availableCountries[Math.floor(Math.random() * this.availableCountries.length)];
        this.searchPlaceholder = this.$t('timeline.e_g', { location: randomCountry });
      } else {
        this.searchPlaceholder = "";
      }
    },
    async loadInitialMemory() {
      this.loading = true;
      try {
        let today = new Date();
        
        // 1. Create an array of 1 to 20 years and shuffle it randomly
        let yearsToCheck = Array.from({ length: 20 }, (_, i) => i + 1);
        yearsToCheck.sort(() => Math.random() - 0.5);
        
        // 2. Check anniversaries in random order
        for (let yearsAgo of yearsToCheck) {
          let pastDate = new Date();
          pastDate.setFullYear(today.getFullYear() - yearsAgo);
          let dateStr = pastDate.toISOString().split('T')[0];
          let res = await Location.get(dateStr);
          
          if (res.isOK && res.data && res.data.length > 0) {
            this.setupMemoryView(dateStr, res.data, yearsAgo);
            return;
          }
        }

        // 3. Fallback to a random day where points exist.
        let countRes = await storage.count("locations", []); 
        if (countRes.isOK && countRes.data > 0) {
          let randOffset = Math.floor(Math.random() * countRes.data);
          let randomPointRes = await storage.get("locations", [], { column: "date", order: "desc" }, randOffset, 1);
          if (randomPointRes.isOK && randomPointRes.data && randomPointRes.data.length > 0) {
            let randDate = randomPointRes.data[0].data.date;
            let res4 = await Location.get(randDate);
            if (res4.isOK && res4.data && res4.data.length > 0) {
              this.setupMemoryView(randDate, res4.data, null);
              return;
            }
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        this.loading = false;
      }
    },
    setupMemoryView(date, locations, yearsAgo) {
      this.locations = locations;
      this.memoryDate = date;
      this.memoryYearsAgo = yearsAgo;
      this.isMemory = true;
      
      let addresses = new Set();
      this.path = [];
      let groups = {};
      
      this.locations.forEach(location => {
        this.path.push({
          lat: parseFloat(location.data.lat),
          lng: parseFloat(location.data.lon),
        });

        if (location.data.address) {
          let addrParts = location.data.address.split(',');
          let cityCountry = addrParts.length >= 2 
            ? addrParts[addrParts.length - 2].trim() + ", " + addrParts[addrParts.length - 1].trim()
            : location.data.address;
          addresses.add(cityCountry);
        }

        if (!groups[location.data.date]) {
          groups[location.data.date] = [];
        }
        groups[location.data.date].push(location);
      });
      
      this.uniqueMemoryAddresses = Array.from(addresses).slice(0, 5);

      this.aggregatedLocations = Object.keys(groups).map(d => {
        return {
          date: d,
          count: groups[d].length,
          firstPoint: groups[d][0]
        };
      }).sort((a, b) => a.date.localeCompare(b.date));
      
      this.searched = false;
    },
    async search() {
      if (!this.searchQuery) {
        Notifications.show(this.$t("timeline.notification.enter_address_to_search"));
        return;
      }

      if (
        this.$route.query.q !== this.searchQuery || 
        this.$route.query.field !== this.searchField ||
        this.$route.query.start !== this.startDate ||
        this.$route.query.end !== this.endDate
      ) {
        let queryObj = { ...this.$route.query, q: this.searchQuery, field: this.searchField };
        if (this.startDate) queryObj.start = this.startDate;
        else delete queryObj.start;
        if (this.endDate) queryObj.end = this.endDate;
        else delete queryObj.end;
        
        this.$router.replace({ query: queryObj }).catch(() => {});
      }

      this.loading = true;
      this.searched = false;
      this.isMemory = false;
      this.locations = [];
      this.aggregatedLocations = [];
      this.path = [];
      this.lastSearchQuery = this.searchQuery;

      try {
        var response = await Location.search(this.searchQuery);

        if (response.isOK && response.data !== undefined) {
          let results = response.data;
          
          if (this.searchField === 'city') {
            results = results.filter(loc => {
              let address = loc.data.address || "";
              let parts = address.split(',');
              let cityPart = parts.length > 1 ? parts[parts.length - 2].trim() : parts[0].trim();
              let city = cityPart.replace(/[0-9]/g, '').trim();
              return city.toLowerCase().includes(this.searchQuery.toLowerCase());
            });
          } else if (this.searchField === 'country') {
            results = results.filter(loc => {
              let address = loc.data.address || "";
              let parts = address.split(',');
              let country = parts[parts.length - 1].replace(/[0-9]/g, '').trim();
              return country.toLowerCase().includes(this.searchQuery.toLowerCase());
            });
          }
          
          if (this.startDate) {
            results = results.filter(loc => loc.data.date >= this.startDate);
          }
          if (this.endDate) {
            results = results.filter(loc => loc.data.date <= this.endDate);
          }

          this.locations = results;
          
          let groups = {};
          this.locations.forEach(location => {
            this.path.push({
              lat: parseFloat(location.data.lat),
              lng: parseFloat(location.data.lon),
            });

            if (!groups[location.data.date]) {
              groups[location.data.date] = [];
            }
            groups[location.data.date].push(location);
          });

          this.aggregatedLocations = Object.keys(groups).map(date => {
            return {
              date: date,
              count: groups[date].length,
              firstPoint: groups[date][0]
            };
          }).sort((a, b) => a.date.localeCompare(b.date));
        }
      } catch (err) {
        console.error(err);
        Notifications.show(this.$t("timeline.notification.error_fetching_search_results"));
      } finally {
        this.loading = false;
        this.searched = true;
        this.loadPlaceholderOptions();
      }
    },
    
    getCenterFromCoordinates(coordinates) {
      if (coordinates.length === 0) return { lat: 0, lng: 0 };
      var lats = 0,
        lngs = 0;
      coordinates.forEach(function (curr) {
        lats += curr.lat;
        lngs += curr.lng;
      });
      return { lat: lats / coordinates.length, lng: lngs / coordinates.length };
    },
    getZoomLevel(coordinates) {
      if (coordinates.length === 0) return 2;
      var GLOBE_WIDTH = 256;
      var pixelWidth = window.innerWidth / 3;
      var pixelHeight = 300;
      var west = 180,
        east = -180,
        north = 180,
        south = -180;
      coordinates.forEach(function (curr) {
        if (curr.lng < west) {
          west = curr.lng;
        }
        if (curr.lng > east) {
          east = curr.lng;
        }

        if (curr.lat < north) {
          north = curr.lat;
        }
        if (curr.lat > south) {
          south = curr.lat;
        }
      });
      var angle = east - west;
      if (angle < 0) {
        angle += 360;
      }

      var angle2 = south - north;
      if (angle2 < 0) {
        angle2 += 360;
      }

      var result = Math.round(
          (Math.round(
            Math.log((pixelWidth * 360) / angle / GLOBE_WIDTH) / Math.LN2
          ) +
            Math.round(
              Math.log((pixelHeight * 360) / angle2 / GLOBE_WIDTH) / Math.LN2
            )) /
            2
        ) - 2;
      
      return isNaN(result) || result === -Infinity ? 2 : result;
    },

    async deletePoint(point) {
      if (confirm(this.$t("timeline.do_you_really_want_to_delete_this_point"))) {
        var delete_response = await storage.delete("locations", point.id);
        if (delete_response.isOK) {
          Notifications.show(this.$t("timeline.notification.point_deleted"));
          this.search(); // Refresh results
        } else {
          Notifications.show(this.$t("timeline.notification.error_deleting_point"));
        }
      }
    },
    resetSearch() {
      this.searchQuery = '';
      this.startDate = '';
      this.endDate = '';
      this.searched = false;
      if (this.$route.query.q || this.$route.query.field || this.$route.query.start || this.$route.query.end) {
        let newQuery = { ...this.$route.query };
        delete newQuery.q;
        delete newQuery.field;
        delete newQuery.start;
        delete newQuery.end;
        this.$router.replace({ query: newQuery }).catch(() => {});
      }
      this.loadInitialMemory();
    },
    switchView() {
      this.currentView = this.currentView === 'aggregated' ? 'individual' : 'aggregated';
    },
    openMemoryDay() {
      if (this.memoryDate) {
        this.$router.push(`/timeline/${this.memoryDate}`);
      }
    },
    openDailyView(period) {
      if (period.rawFirstDate === period.rawLastDate) {
        this.$router.push(`/timeline/${period.rawFirstDate}`);
      } else {
        this.$router.push(`/timeline/${period.rawFirstDate}/${period.rawLastDate}`);
      }
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const parts = dateString.split('-');
      if (parts.length === 3) {
        return `${parts[2]}.${parts[1]}.${parts[0]}`;
      }
      return dateString;
    }
  },
  components: {
    PointSingle,
  },
};
</script>

<style scoped>
.empty_map {
  height: 400px;
  width: calc(100% + 2 * 12px);
  margin: -12px;
  background: rgba(128, 128, 128, 0.15);
}

.radar-spinner {
  position: relative;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.radar-dot {
  position: relative;
  z-index: 3;
  width: 40px;
  height: 40px;
  background: var(--v-primary-base, #1976D2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.4);
  animation: radar-bounce 1.5s infinite cubic-bezier(0.28, 0.84, 0.42, 1);
}

.radar-ring {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background: var(--v-primary-base, #1976D2);
  opacity: 0;
}

.radar-ring-1 {
  width: 40px;
  height: 40px;
  animation: radar-pulse 2s infinite cubic-bezier(0.215, 0.61, 0.355, 1);
}

.radar-ring-2 {
  width: 40px;
  height: 40px;
  animation: radar-pulse 2s infinite cubic-bezier(0.215, 0.61, 0.355, 1);
  animation-delay: 0.6s;
}

@keyframes radar-bounce {
  0%, 100% {
    transform: translateY(0) scale(1);
  }
  50% {
    transform: translateY(-8px) scale(1.05);
  }
}

@keyframes radar-pulse {
  0% {
    width: 40px;
    height: 40px;
    opacity: 0.6;
  }
  100% {
    width: 140px;
    height: 140px;
    opacity: 0;
  }
}

.timeline-card {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  border-radius: 16px !important;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.timeline-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 20px rgba(0,0,0,0.08) !important;
}

@keyframes slideUpFade {
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.timeline-item-animated {
  animation: slideUpFade 0.6s cubic-bezier(0.16, 1, 0.3, 1) backwards;
}

.insight-period-item {
  transition: color 0.2s ease, opacity 0.2s ease;
}

.insight-period-item:hover {
  color: var(--v-primary-base, #1976D2);
  opacity: 0.8;
}
</style>
